import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wrench } from 'lucide-react';

interface AnalyticsEvent {
  event_type: string;
  session_id: string;
  event_data: Record<string, unknown>;
}

interface ToolUsageTableProps {
  events: AnalyticsEvent[];
}

const ToolUsageTable = ({ events }: ToolUsageTableProps) => {
  // Find sessions that visited tool pages
  const sessionTools = new Map<string, string>();
  events.forEach(e => {
    if (e.event_type === 'page_view' && e.event_data?.page_type === 'tool_page' && e.event_data?.tool_slug) {
      sessionTools.set(e.session_id, String(e.event_data.tool_slug));
    }
    // Also map from tool events that carry tool_slug directly
    if (['tool_start', 'tool_success', 'upload_success', 'download_success'].includes(e.event_type) && e.event_data?.tool_slug) {
      if (!sessionTools.has(e.session_id)) {
        sessionTools.set(e.session_id, String(e.event_data.tool_slug));
      }
    }
  });

  // Aggregate per tool
  const toolMetrics = new Map<string, { uploads: number; compressions: number; downloads: number }>();

  events.forEach(e => {
    const tool = sessionTools.get(e.session_id);
    if (!tool) return;

    if (!toolMetrics.has(tool)) {
      toolMetrics.set(tool, { uploads: 0, compressions: 0, downloads: 0 });
    }
    const m = toolMetrics.get(tool)!;

    if (e.event_type === 'upload_success') m.uploads++;
    else if (['compression_success', 'tool_success', 'crop_apply', 'rotate_apply'].includes(e.event_type)) m.compressions++;
    else if (['download_click', 'download_success', 'batch_download_zip'].includes(e.event_type)) m.downloads++;
  });

  const rows = Array.from(toolMetrics.entries())
    .map(([tool, m]) => ({ tool, ...m }))
    .sort((a, b) => b.compressions - a.compressions);

  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5" />
          Uso por Herramienta
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tool Slug</TableHead>
              <TableHead className="text-right">Uploads</TableHead>
              <TableHead className="text-right">Compresiones</TableHead>
              <TableHead className="text-right">Descargas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.tool}>
                <TableCell className="text-sm font-mono">{r.tool}</TableCell>
                <TableCell className="text-right">{r.uploads}</TableCell>
                <TableCell className="text-right">{r.compressions}</TableCell>
                <TableCell className="text-right">{r.downloads}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ToolUsageTable;
